import { useState, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Pencil,
  Mail,
  Webhook,
  Plus,
  Trash2,
  Bell,
  BellOff,
  Copy,
  CheckCircle,
  Database,
  FileText,
  FileSpreadsheet,
  Settings,
  Play,
  Loader2,
  Sparkles,
  Zap,
  PenLine,
  TextSelect,
  MoreVertical,
  Info,
} from 'lucide-react'
import type {
  EditStreamComponentProps,
  StreamDetail,
  Source,
  SourceType,
  Question,
  ResponseFormat,
  GemCriterion,
  EmailSourceConfig,
  WebhookSourceConfig,
  AccountUser,
  GemCriteriaTemplate,
} from '@/types/streams'
import { SaveAsTemplateModal, ConfirmOverwriteModal } from '../gem-criteria'

interface CollapsibleSectionProps {
  title: string
  count: number
  isExpanded: boolean
  onToggle: () => void
  children: React.ReactNode
}

function CollapsibleSection({
  title,
  count,
  isExpanded,
  onToggle,
  children,
}: CollapsibleSectionProps) {
  return (
    <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-5 text-left"
      >
        <div className="flex items-center gap-2.5">
          <span className="text-sm font-medium text-stone-900 dark:text-stone-100">
            {title}
          </span>
          <span className="text-xs text-stone-400 dark:text-stone-500">
            {count}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-stone-400 transition-transform duration-200 ${
            isExpanded ? '' : '-rotate-90'
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-6 pt-2">{children}</div>
      </div>
    </div>
  )
}

const SourceIcon = ({ type }: { type: SourceType }) => {
  const iconClass = 'w-4 h-4'
  if (type === 'email') return <Mail className={iconClass} />
  return <Webhook className={iconClass} />
}

interface UserCheckboxListProps {
  users: AccountUser[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  label?: string
}

function UserCheckboxList({ users, selectedIds, onChange, label }: UserCheckboxListProps) {
  const toggleUser = (userId: string) => {
    if (selectedIds.includes(userId)) {
      onChange(selectedIds.filter(id => id !== userId))
    } else {
      onChange([...selectedIds, userId])
    }
  }

  if (!users || users.length === 0) {
    return (
      <p className="text-xs text-stone-400 dark:text-stone-500 italic">
        No users available
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide">
          {label}
        </label>
      )}
      <div className="space-y-1">
        {users.map(user => (
          <label key={user.id} className="flex items-center gap-2 cursor-pointer py-1">
            <input
              type="checkbox"
              checked={selectedIds.includes(user.id)}
              onChange={() => toggleUser(user.id)}
              className="w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-stone-700 dark:text-stone-300">
              {user.name}
              {user.isOwner && (
                <span className="ml-1 text-xs text-stone-400">(Owner)</span>
              )}
            </span>
            <span className="text-xs text-stone-400">{user.email}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

interface SourceItemProps {
  source: Source
  copiedSourceId: string | null
  onCopy: (text: string, sourceId: string) => void
}

function SourceItem({ source, copiedSourceId, onCopy }: SourceItemProps) {
  const [jsonMappingExpanded, setJsonMappingExpanded] = useState(false)

  return (
    <div className="pt-4 pb-3 border-b border-stone-100 dark:border-stone-800 last:border-0 first:pt-2">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-stone-500 dark:text-stone-400">
          <SourceIcon type={source.type} />
        </div>
        <p className="text-sm font-medium text-stone-900 dark:text-stone-100">
          {source.type === 'email' ? 'Source items via email' : 'Source items via webhook'}
        </p>
      </div>

      {/* Email address display */}
      {source.type === 'email' && 'generatedAddress' in source.config && (
        <div className="mt-1.5 ml-11">
          <div className="flex items-center gap-2 p-2 rounded-md bg-stone-50 dark:bg-stone-800/50">
            <code className="flex-1 text-xs text-stone-600 dark:text-stone-400 font-mono truncate">
              {(source.config as EmailSourceConfig).generatedAddress}
            </code>
            <button
              onClick={() => onCopy((source.config as EmailSourceConfig).generatedAddress, source.id)}
              className={`p-1.5 rounded transition-all flex-shrink-0 ${
                copiedSourceId === source.id
                  ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950'
                  : 'text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700'
              }`}
              title="Copy email address"
            >
              {copiedSourceId === source.id ? (
                <CheckCircle className="w-3.5 h-3.5" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Webhook credentials display */}
      {source.type === 'webhook' && 'webhookUrl' in source.config && 'webhookToken' in source.config && (source.config as WebhookSourceConfig).webhookToken && (
        <div className="mt-1.5 ml-11 space-y-2">
          <div className="flex items-center gap-2 p-2 rounded-md bg-stone-50 dark:bg-stone-800/50">
            <code className="flex-1 text-xs text-stone-600 dark:text-stone-400 font-mono truncate">
              {`${(source.config as WebhookSourceConfig).webhookUrl}?token=•••••••••••`}
            </code>
            <button
              onClick={() => onCopy(
                `${(source.config as WebhookSourceConfig).webhookUrl}?token=${(source.config as WebhookSourceConfig).webhookToken}`,
                `${source.id}-url`
              )}
              className={`p-1.5 rounded transition-all flex-shrink-0 ${
                copiedSourceId === `${source.id}-url`
                  ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950'
                  : 'text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700'
              }`}
              title="Copy full webhook URL with token"
            >
              {copiedSourceId === `${source.id}-url` ? (
                <CheckCircle className="w-3.5 h-3.5" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>
          </div>

          {/* Collapsible JSON Key Mapping */}
          <div>
            <button
              onClick={() => setJsonMappingExpanded(!jsonMappingExpanded)}
              className="flex items-center gap-1.5 text-xs text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-400 transition-colors"
            >
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  jsonMappingExpanded ? '' : '-rotate-90'
                }`}
              />
              <span>JSON data mapping...</span>
            </button>
            <div
              className={`overflow-hidden transition-all duration-200 ${
                jsonMappingExpanded ? 'max-h-[500px] opacity-100 mt-2' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="rounded-md bg-teal-50 dark:bg-teal-950/30 border border-teal-100 dark:border-teal-900/50 p-3 space-y-2">
                <p className="text-xs text-teal-600 dark:text-teal-400">
                  Send POST requests with a JSON body. We recognize these keys:
                </p>
                <div className="space-y-1 text-xs">
                  <div className="flex items-start gap-2">
                    <span className="text-teal-500 dark:text-teal-400 w-14 flex-shrink-0">Sender:</span>
                    <span className="text-teal-700 dark:text-teal-300 font-mono">from, sender, author, email</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-teal-500 dark:text-teal-400 w-14 flex-shrink-0">Subject:</span>
                    <span className="text-teal-700 dark:text-teal-300 font-mono">subject, title, heading, name</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-teal-500 dark:text-teal-400 w-14 flex-shrink-0">Content:</span>
                    <span className="text-teal-700 dark:text-teal-300 font-mono">content, body, message, text, description</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Simple tooltip component
function Tooltip({ children, content }: { children: React.ReactNode; content: string }) {
  const [show, setShow] = useState(false)

  return (
    <div className="relative inline-flex">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-stone-900 dark:bg-stone-700 rounded-lg shadow-lg z-50 w-64 text-center">
          {content}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-stone-900 dark:border-t-stone-700" />
        </div>
      )}
    </div>
  )
}

// Stream actions dropdown menu
interface StreamActionsDropdownProps {
  streamId: string
  onUploadCsv: () => void
  onDelete: () => void
}

function StreamActionsDropdown({ streamId, onUploadCsv, onDelete }: StreamActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-stone-800 rounded-xl shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-50">
          <button
            onClick={() => {
              router.visit(`/streams/${streamId}/settings`)
              setIsOpen(false)
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
          <button
            onClick={() => {
              onUploadCsv()
              setIsOpen(false)
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Upload CSV
          </button>
          <button
            onClick={() => {
              router.visit(`/reports?stream=${streamId}`)
              setIsOpen(false)
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
          >
            <FileText className="w-4 h-4" />
            Reports
          </button>
          <button
            onClick={() => {
              router.visit(`/streams/${streamId}/gems`)
              setIsOpen(false)
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-amber-600 dark:text-amber-400 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Gems
          </button>
          <div className="my-1 border-t border-stone-100 dark:border-stone-700" />
          <button
            onClick={() => {
              onDelete()
              setIsOpen(false)
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

interface EditStreamProps extends EditStreamComponentProps {
  gemCriteriaTemplates?: GemCriteriaTemplate[]
}

export function EditStream({
  stream,
  onSave,
  onCancel,
  onDelete,
  onToggleStatus,
  onUploadCsv,
  gemCriteriaTemplates = [],
}: EditStreamProps) {
  const handleViewItems = () => {
    router.visit(`/items?stream=${stream.id}`)
  }

  const handleViewReports = () => {
    router.visit(`/reports?stream=${stream.id}`)
  }
  // Local state for editing
  const [name, setName] = useState(stream.name)
  const [description, setDescription] = useState(stream.description || '')
  const [status, setStatus] = useState(stream.status)
  // Sources are read-only - auto-created as email and webhook on stream creation
  // Filter out CSV sources - they're handled separately via Upload CSV button
  const sources = stream.sources.filter(s => s.type === 'email' || s.type === 'webhook')
  const [questions, setQuestions] = useState<Question[]>(stream.questions)
  const [gemCriteria, setGemCriteria] = useState<GemCriterion[]>(stream.gemCriteria)
  const [reportSubscriberIds, setReportSubscriberIds] = useState<string[]>(stream.reportSubscriberIds || [])

  // UI state
  const [isEditingName, setIsEditingName] = useState(false)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['sources', 'questions', 'gemCriteria'])
  )
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  // Add forms state
  const [showQuestionForm, setShowQuestionForm] = useState(false)
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null)
  const [newQuestionText, setNewQuestionText] = useState('')
  const [newQuestionDescription, setNewQuestionDescription] = useState('')
  const [newQuestionFormat, setNewQuestionFormat] = useState<ResponseFormat>('single')

  const [showCriteriaForm, setShowCriteriaForm] = useState(false)
  const [editingCriteriaId, setEditingCriteriaId] = useState<string | null>(null)
  const [newCriteriaTitle, setNewCriteriaTitle] = useState('')
  const [newCriteriaDescription, setNewCriteriaDescription] = useState('')
  const [newCriteriaNotify, setNewCriteriaNotify] = useState(false)
  const [newCriteriaSubscriberIds, setNewCriteriaSubscriberIds] = useState<string[]>([])

  // Template state
  const [templates, setTemplates] = useState<GemCriteriaTemplate[]>(gemCriteriaTemplates)
  const [showTemplateChoice, setShowTemplateChoice] = useState(false)
  const [saveAsTemplateModalOpen, setSaveAsTemplateModalOpen] = useState(false)
  const [criteriaToSaveAsTemplate, setCriteriaToSaveAsTemplate] = useState<GemCriterion | null>(null)
  const [confirmOverwriteModalOpen, setConfirmOverwriteModalOpen] = useState(false)
  const [templateToApply, setTemplateToApply] = useState<GemCriteriaTemplate | null>(null)

  const [copiedSourceId, setCopiedSourceId] = useState<string | null>(null)

  // Check if there are unsaved changes (sources are read-only, not tracked here)
  const hasChanges =
    name !== stream.name ||
    description !== (stream.description || '') ||
    status !== stream.status ||
    JSON.stringify(questions) !== JSON.stringify(stream.questions) ||
    JSON.stringify(gemCriteria) !== JSON.stringify(stream.gemCriteria) ||
    JSON.stringify(reportSubscriberIds.sort()) !== JSON.stringify((stream.reportSubscriberIds || []).sort())

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  const handleSave = () => {
    const updatedStream: StreamDetail = {
      ...stream,
      name,
      description: description || null,
      status,
      sources,
      questions,
      gemCriteria,
      reportSubscriberIds,
    }
    onSave?.(updatedStream)
  }

  const handleGenerateReport = async () => {
    setIsGenerating(true)
    try {
      router.post(`/streams/${stream.id}/generate_report`, {}, {
        onSuccess: () => {
          setShowGenerateConfirm(false)
          setIsGenerating(false)
        },
        onError: () => {
          setIsGenerating(false)
        }
      })
    } catch {
      setIsGenerating(false)
    }
  }

  // Format short date with time for schedule icons
  const formatShortDate = (dateStr: string | null) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'pm' : 'am'
    const hour12 = hours % 12 || 12
    const timePart = minutes > 0 ? `${hour12}:${minutes.toString().padStart(2, '0')}${ampm}` : `${hour12}${ampm}`
    return `${datePart} · ${timePart}`
  }

  const nextScheduledReport = formatShortDate(stream.nextScheduledReportAt)
  const nextDelivery = formatShortDate(stream.nextDeliveryAt)

  const handleStatusToggle = () => {
    const newStatus = status === 'active' ? 'paused' : 'active'
    setStatus(newStatus)
    onToggleStatus?.()
  }

  const copyToClipboard = (text: string, sourceId: string) => {
    navigator.clipboard.writeText(text)
    setCopiedSourceId(sourceId)
    setTimeout(() => setCopiedSourceId(null), 2000)
  }

  const resetQuestionForm = () => {
    setShowQuestionForm(false)
    setEditingQuestionId(null)
    setNewQuestionText('')
    setNewQuestionDescription('')
    setNewQuestionFormat('single')
  }

  const addQuestion = () => {
    if (!newQuestionText.trim()) return
    const newQ: Question = {
      id: `q-${Date.now()}`,
      text: newQuestionText,
      description: newQuestionDescription || null,
      responseFormat: newQuestionFormat,
    }
    setQuestions([...questions, newQ])
    resetQuestionForm()
  }

  const startEditQuestion = (q: Question) => {
    setEditingQuestionId(q.id)
    setNewQuestionText(q.text)
    setNewQuestionDescription(q.description || '')
    setNewQuestionFormat(q.responseFormat)
    setShowQuestionForm(true)
  }

  const saveEditQuestion = () => {
    if (!newQuestionText.trim() || !editingQuestionId) return
    setQuestions(questions.map((q) =>
      q.id === editingQuestionId
        ? { ...q, text: newQuestionText, description: newQuestionDescription || null, responseFormat: newQuestionFormat }
        : q
    ))
    resetQuestionForm()
  }

  const removeQuestion = (id: string) => {
    setQuestions(questions.filter((q) => q.id !== id))
  }

  const moveQuestion = (id: string, direction: 'up' | 'down') => {
    const index = questions.findIndex((q) => q.id === id)
    if (index === -1) return
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === questions.length - 1) return

    const newQuestions = [...questions]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[newQuestions[index], newQuestions[swapIndex]] = [newQuestions[swapIndex], newQuestions[index]]
    setQuestions(newQuestions)
  }

  const resetCriteriaForm = () => {
    setShowCriteriaForm(false)
    setEditingCriteriaId(null)
    setNewCriteriaTitle('')
    setNewCriteriaDescription('')
    setNewCriteriaNotify(false)
    setNewCriteriaSubscriberIds([])
    setShowTemplateChoice(false)
  }

  // Template helper functions
  const handleSelectTemplate = (template: GemCriteriaTemplate) => {
    // If editing an existing criteria, show confirm dialog
    if (editingCriteriaId) {
      setTemplateToApply(template)
      setConfirmOverwriteModalOpen(true)
    } else {
      // For new criteria, just pre-fill the form
      setNewCriteriaTitle(template.title)
      setNewCriteriaDescription(template.description)
      setShowCriteriaForm(true)
      setShowTemplateChoice(false)
    }
  }

  const handleConfirmApplyTemplate = () => {
    if (templateToApply) {
      setNewCriteriaTitle(templateToApply.title)
      setNewCriteriaDescription(templateToApply.description)
      setTemplateToApply(null)
    }
  }

  const handleCreateFromScratch = () => {
    setShowCriteriaForm(true)
    setShowTemplateChoice(false)
  }

  const openSaveAsTemplateModal = (gc: GemCriterion) => {
    setCriteriaToSaveAsTemplate(gc)
    setSaveAsTemplateModalOpen(true)
  }

  const handleTemplateSaved = (template: GemCriteriaTemplate) => {
    setTemplates([...templates, template])
  }

  // Get owner user ID for default selection
  const getOwnerUserId = (): string | null => {
    const owner = stream.accountUsers?.find(u => u.isOwner)
    return owner?.id || null
  }

  const addCriteria = () => {
    if (!newCriteriaTitle.trim() || !newCriteriaDescription.trim()) return
    // If notify is enabled but no subscribers selected, add owner by default
    let subscriberIds = newCriteriaSubscriberIds
    if (newCriteriaNotify && subscriberIds.length === 0) {
      const ownerId = getOwnerUserId()
      if (ownerId) subscriberIds = [ownerId]
    }
    const newGc: GemCriterion = {
      id: `gc-${Date.now()}`,
      title: newCriteriaTitle,
      description: newCriteriaDescription,
      notifyImmediately: newCriteriaNotify,
      subscriberIds: subscriberIds,
    }
    setGemCriteria([...gemCriteria, newGc])
    resetCriteriaForm()
  }

  const startEditCriteria = (gc: GemCriterion) => {
    setEditingCriteriaId(gc.id)
    setNewCriteriaTitle(gc.title)
    setNewCriteriaDescription(gc.description)
    setNewCriteriaNotify(gc.notifyImmediately)
    setNewCriteriaSubscriberIds(gc.subscriberIds || [])
    setShowCriteriaForm(true)
  }

  const saveEditCriteria = () => {
    if (!newCriteriaTitle.trim() || !newCriteriaDescription.trim() || !editingCriteriaId) return
    // If notify is enabled but no subscribers selected, add owner by default
    let subscriberIds = newCriteriaSubscriberIds
    if (newCriteriaNotify && subscriberIds.length === 0) {
      const ownerId = getOwnerUserId()
      if (ownerId) subscriberIds = [ownerId]
    }
    setGemCriteria(gemCriteria.map((gc) =>
      gc.id === editingCriteriaId
        ? { ...gc, title: newCriteriaTitle, description: newCriteriaDescription, notifyImmediately: newCriteriaNotify, subscriberIds: subscriberIds }
        : gc
    ))
    resetCriteriaForm()
  }

  const removeCriteria = (id: string) => {
    setGemCriteria(gemCriteria.filter((gc) => gc.id !== id))
  }

  const moveCriteria = (id: string, direction: 'up' | 'down') => {
    const index = gemCriteria.findIndex((gc) => gc.id === id)
    if (index === -1) return
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === gemCriteria.length - 1) return

    const newCriteria = [...gemCriteria]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[newCriteria[index], newCriteria[swapIndex]] = [newCriteria[swapIndex], newCriteria[index]]
    setGemCriteria(newCriteria)
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900">
        <div className="max-w-4xl mx-auto px-6 py-5">
          {/* Breadcrumb */}
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Streams</span>
          </button>

          {/* Title and controls row */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="flex-1 text-2xl font-semibold bg-transparent border-b-2 border-teal-500 focus:outline-none text-stone-900 dark:text-stone-100 pb-1"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setIsEditingName(false)
                      if (e.key === 'Escape') {
                        setName(stream.name)
                        setIsEditingName(false)
                      }
                    }}
                    onBlur={() => setIsEditingName(false)}
                  />
                </div>
              ) : (
                <button
                  onClick={() => setIsEditingName(true)}
                  className="group flex items-center gap-2 text-left -ml-1 px-1 rounded hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                >
                  <h1 className="text-2xl font-semibold text-stone-900 dark:text-stone-100">
                    {name}
                  </h1>
                  <Pencil className="w-4 h-4 text-stone-300 dark:text-stone-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              )}

              {/* Description - only show if there is one */}
              {description && (
                <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed mt-1">
                  {description}
                </p>
              )}
            </div>

            {/* Status toggle and actions dropdown */}
            <div className="flex items-center gap-3 pt-1">
              {/* Schedule badges - link to settings */}
              {(nextScheduledReport || nextDelivery) && (
                <button
                  onClick={() => router.visit(`/streams/${stream.id}/settings`)}
                  className="hidden sm:inline-flex items-center gap-3 px-3 py-1.5 rounded-lg bg-stone-50 dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                  title="Edit schedules"
                >
                  {nextScheduledReport && (
                    <span className="flex items-center gap-1.5" title="Next scheduled report generation">
                      <Zap className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-xs font-medium text-stone-600 dark:text-stone-400">{nextScheduledReport}</span>
                    </span>
                  )}
                  {nextDelivery && (
                    <span className="flex items-center gap-1.5" title="Next report delivery">
                      <Mail className="w-3.5 h-3.5 text-teal-500" />
                      <span className="text-xs font-medium text-stone-600 dark:text-stone-400">{nextDelivery}</span>
                    </span>
                  )}
                </button>
              )}

              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-500 dark:text-stone-400 hidden sm:inline">
                  {status === 'active' ? 'Active' : 'Paused'}
                </span>
                <button
                  onClick={handleStatusToggle}
                  role="switch"
                  aria-checked={status === 'active'}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:focus:ring-offset-stone-900 ${
                    status === 'active'
                      ? 'bg-emerald-500'
                      : 'bg-stone-200 dark:bg-stone-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                      status === 'active' ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <StreamActionsDropdown
                streamId={stream.id}
                onUploadCsv={onUploadCsv}
                onDelete={() => setShowDeleteConfirm(true)}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Consolidated Actions Bar */}
      <div className={`bg-white dark:bg-stone-900 border-t border-stone-200 dark:border-stone-800 ${stream.currentCharacterCount === 0 ? 'border-b' : ''}`}>
        <div className="max-w-4xl mx-auto px-6 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Left side buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleViewReports}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
              >
                <FileText className="w-4 h-4" />
                <span>Reports</span>
                <span className="text-stone-400 dark:text-stone-500">({stream.reportCount})</span>
              </button>
              <button
                onClick={handleViewItems}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
              >
                <Database className="w-4 h-4" />
                <span>Items</span>
                <span className="text-stone-400 dark:text-stone-500">({stream.itemCount.toLocaleString()})</span>
              </button>
              <button
                onClick={() => router.visit(`/streams/${stream.id}/gems`)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-400 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                <span>Gems</span>
              </button>
            </div>

            {/* Right side - Generate button */}
            <button
              onClick={() => setShowGenerateConfirm(true)}
              disabled={stream.unprocessedItemsCount === 0}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 dark:disabled:bg-stone-600 text-white disabled:text-stone-500 dark:disabled:text-stone-400 rounded-lg transition-all shadow-sm hover:shadow disabled:shadow-none disabled:cursor-not-allowed"
            >
              <Play className="w-4 h-4" />
              Generate Now
            </button>
          </div>
        </div>
      </div>

      {/* Progress Meter */}
      {stream.currentCharacterCount > 0 && (
        <div className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
          <div className="max-w-4xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between text-xs text-stone-500 dark:text-stone-400 mb-1.5">
              <div className="flex items-center gap-1.5">
                <span>{stream.unprocessedItemsCount} items pending analysis</span>
                <Tooltip content="Report will auto-generate when total characters across all unprocessed items reaches the threshold.">
                  <Info className="w-3.5 h-3.5 text-stone-400 dark:text-stone-500 cursor-help" />
                </Tooltip>
              </div>
              <span>{stream.currentCharacterCount.toLocaleString()} / {stream.characterThreshold.toLocaleString()} chars</span>
            </div>
            <div className="w-full h-2 bg-stone-200 dark:bg-stone-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-teal-600 dark:bg-teal-500 transition-all duration-300"
                style={{ width: `${stream.thresholdPercentage}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Generate Report Confirmation Dialog */}
      {showGenerateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-stone-900 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 py-5 border-b border-stone-200 dark:border-stone-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
                    Generate New Report
                  </h2>
                  <p className="text-sm text-stone-500 dark:text-stone-400">
                    {stream.name}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 px-4 bg-stone-50 dark:bg-stone-800/50 rounded-lg">
                  <span className="text-sm text-stone-600 dark:text-stone-400">Items to analyze</span>
                  <span className="text-sm font-semibold text-stone-900 dark:text-stone-100">
                    {stream.unprocessedItemsCount}
                  </span>
                </div>
                <p className="text-sm text-stone-600 dark:text-stone-400">
                  This will create a new report by analyzing all unprocessed items using AI.
                  The report will appear in your reports list once complete.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-stone-50 dark:bg-stone-800/30 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowGenerateConfirm(false)}
                disabled={isGenerating}
                className="px-4 py-2 text-sm text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateReport}
                disabled={isGenerating}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/70 text-white rounded-lg transition-colors"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Generate Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-3">
        {/* Sources Section - Read-only display */}
        <CollapsibleSection
          title="Sources"
          count={sources.length}
          isExpanded={expandedSections.has('sources')}
          onToggle={() => toggleSection('sources')}
        >
          <div className="space-y-2">
            {sources.length === 0 ? (
              <div className="py-4 text-center">
                <p className="text-sm text-stone-400 dark:text-stone-500">
                  No sources configured
                </p>
              </div>
            ) : (
              <>
                {sources.map((source) => (
                  <SourceItem
                    key={source.id}
                    source={source}
                    copiedSourceId={copiedSourceId}
                    onCopy={copyToClipboard}
                  />
                ))}
              </>
            )}
          </div>
        </CollapsibleSection>

        {/* Questions Section */}
        <CollapsibleSection
          title="Questions"
          count={questions.length}
          isExpanded={expandedSections.has('questions')}
          onToggle={() => toggleSection('questions')}
        >
          <div className="space-y-2">
            {questions.length === 0 && !showQuestionForm ? (
              <div className="py-4 text-center">
                <p className="text-sm text-stone-400 dark:text-stone-500 mb-3">
                  No questions defined
                </p>
                <button
                  onClick={() => setShowQuestionForm(true)}
                  className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium"
                >
                  + Add a question
                </button>
              </div>
            ) : (
              <>
                {questions.map((q, index) => (
                  <div key={q.id}>
                    {editingQuestionId === q.id ? (
                      /* Inline edit form */
                      <div className="my-2 p-4 rounded-lg border-2 border-teal-500 bg-white dark:bg-stone-900 space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                            Question
                          </label>
                          <input
                            type="text"
                            value={newQuestionText}
                            onChange={(e) => setNewQuestionText(e.target.value)}
                            placeholder="What should the AI answer?"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            autoFocus
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                            Description
                            <span className="font-normal normal-case ml-1">(optional)</span>
                          </label>
                          <textarea
                            value={newQuestionDescription}
                            onChange={(e) => setNewQuestionDescription(e.target.value)}
                            placeholder="Additional instructions for AI on how to evaluate this question..."
                            rows={2}
                            className="w-full px-3 py-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                          />
                          <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                            This helps guide the AI when answering the question.
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                            Format
                          </label>
                          <div className="space-y-2">
                            <button
                              onClick={() => setNewQuestionFormat('single')}
                              className={`w-full text-left p-3 rounded-md transition-all ${
                                newQuestionFormat === 'single'
                                  ? 'bg-teal-100 dark:bg-teal-900/50 ring-1 ring-teal-500'
                                  : 'bg-white dark:bg-stone-800 ring-1 ring-stone-200 dark:ring-stone-700 hover:ring-stone-300'
                              }`}
                            >
                              <p className={`text-sm font-medium ${
                                newQuestionFormat === 'single'
                                  ? 'text-teal-700 dark:text-teal-300'
                                  : 'text-stone-700 dark:text-stone-300'
                              }`}>
                                Single answer
                              </p>
                              <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                                Best for questions with one clear answer, like "What is the overall sentiment?"
                              </p>
                            </button>
                            <button
                              onClick={() => setNewQuestionFormat('multiple')}
                              className={`w-full text-left p-3 rounded-md transition-all ${
                                newQuestionFormat === 'multiple'
                                  ? 'bg-teal-100 dark:bg-teal-900/50 ring-1 ring-teal-500'
                                  : 'bg-white dark:bg-stone-800 ring-1 ring-stone-200 dark:ring-stone-700 hover:ring-stone-300'
                              }`}
                            >
                              <p className={`text-sm font-medium ${
                                newQuestionFormat === 'multiple'
                                  ? 'text-teal-700 dark:text-teal-300'
                                  : 'text-stone-700 dark:text-stone-300'
                              }`}>
                                Multiple answers
                              </p>
                              <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                                Best for lists or multiple items, like "What feature requests were mentioned?"
                              </p>
                            </button>
                          </div>
                        </div>

                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={resetQuestionForm}
                            className="px-3 py-1.5 text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={saveEditQuestion}
                            disabled={!newQuestionText.trim()}
                            className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-md disabled:opacity-50 transition-colors"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Display card */
                      <div className="py-3 border-b border-stone-100 dark:border-stone-800 last:border-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <span className="flex-shrink-0 w-5 h-5 rounded bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400 flex items-center justify-center text-xs font-medium">
                              {index + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-stone-900 dark:text-stone-100">
                                {q.text}
                              </p>
                              {q.description && (
                                <p className="text-xs text-stone-500 dark:text-stone-400 mt-1 line-clamp-2">
                                  {q.description}
                                </p>
                              )}
                              <p className="text-xs text-stone-400 dark:text-stone-500 mt-1">
                                {q.responseFormat === 'single'
                                  ? 'Single answer'
                                  : 'Multiple answers'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Reorder buttons */}
                            <div className="flex flex-col">
                              <button
                                onClick={() => moveQuestion(q.id, 'up')}
                                disabled={index === 0}
                                className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => moveQuestion(q.id, 'down')}
                                disabled={index === questions.length - 1}
                                className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                            </div>
                            <button
                              onClick={() => startEditQuestion(q)}
                              className="p-2 text-stone-400 hover:text-teal-500 transition-colors rounded-md hover:bg-stone-100 dark:hover:bg-stone-800"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeQuestion(q.id)}
                              className="p-2 text-stone-400 hover:text-red-500 transition-colors rounded-md hover:bg-stone-100 dark:hover:bg-stone-800 flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add new question form (only when not editing) */}
                {showQuestionForm && !editingQuestionId ? (
                  <div className="mt-3 p-4 rounded-lg bg-stone-50 dark:bg-stone-800/50 space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                        Question
                      </label>
                      <input
                        type="text"
                        value={newQuestionText}
                        onChange={(e) => setNewQuestionText(e.target.value)}
                        placeholder="What should the AI answer?"
                        className="w-full px-3 py-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        autoFocus
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                        Description
                        <span className="font-normal normal-case ml-1">(optional)</span>
                      </label>
                      <textarea
                        value={newQuestionDescription}
                        onChange={(e) => setNewQuestionDescription(e.target.value)}
                        placeholder="Additional instructions for AI on how to evaluate this question..."
                        rows={2}
                        className="w-full px-3 py-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                      <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                        This helps guide the AI when answering the question.
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                        Format
                      </label>
                      <div className="space-y-2">
                        <button
                          onClick={() => setNewQuestionFormat('single')}
                          className={`w-full text-left p-3 rounded-md transition-all ${
                            newQuestionFormat === 'single'
                              ? 'bg-teal-100 dark:bg-teal-900/50 ring-1 ring-teal-500'
                              : 'bg-white dark:bg-stone-800 ring-1 ring-stone-200 dark:ring-stone-700 hover:ring-stone-300'
                          }`}
                        >
                          <p className={`text-sm font-medium ${
                            newQuestionFormat === 'single'
                              ? 'text-teal-700 dark:text-teal-300'
                              : 'text-stone-700 dark:text-stone-300'
                          }`}>
                            Single answer
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                            Best for questions with one clear answer, like "What is the overall sentiment?"
                          </p>
                        </button>
                        <button
                          onClick={() => setNewQuestionFormat('multiple')}
                          className={`w-full text-left p-3 rounded-md transition-all ${
                            newQuestionFormat === 'multiple'
                              ? 'bg-teal-100 dark:bg-teal-900/50 ring-1 ring-teal-500'
                              : 'bg-white dark:bg-stone-800 ring-1 ring-stone-200 dark:ring-stone-700 hover:ring-stone-300'
                          }`}
                        >
                          <p className={`text-sm font-medium ${
                            newQuestionFormat === 'multiple'
                              ? 'text-teal-700 dark:text-teal-300'
                              : 'text-stone-700 dark:text-stone-300'
                          }`}>
                            Multiple answers
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                            Best for lists or multiple items, like "What feature requests were mentioned?"
                          </p>
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={resetQuestionForm}
                        className="px-3 py-1.5 text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={addQuestion}
                        disabled={!newQuestionText.trim()}
                        className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-md disabled:opacity-50 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                ) : !editingQuestionId && (
                  <button
                    onClick={() => setShowQuestionForm(true)}
                    className="w-full mt-2 py-2 text-sm text-stone-400 dark:text-stone-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                  >
                    + Add question
                  </button>
                )}
              </>
            )}
          </div>
        </CollapsibleSection>

        {/* Gem Criteria Section */}
        <CollapsibleSection
          title="Gem Criteria"
          count={gemCriteria.length}
          isExpanded={expandedSections.has('gemCriteria')}
          onToggle={() => toggleSection('gemCriteria')}
        >
          <div className="space-y-2">
            {gemCriteria.length === 0 && !showCriteriaForm ? (
              <div className="py-4 text-center">
                <p className="text-sm text-stone-400 dark:text-stone-500 mb-3">
                  No criteria defined
                </p>
                <button
                  onClick={() => templates.length > 0 ? setShowTemplateChoice(true) : setShowCriteriaForm(true)}
                  className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium"
                >
                  + Add criteria
                </button>
              </div>
            ) : (
              <>
                {gemCriteria.map((gc, index) => (
                  <div key={gc.id}>
                    {editingCriteriaId === gc.id ? (
                      /* Inline edit form */
                      <div className="my-2 p-4 rounded-lg border-2 border-teal-500 bg-white dark:bg-stone-900 space-y-4">
                        {/* Use Template selector for existing criteria */}
                        {templates.length > 0 && (
                          <div className="flex items-center gap-2 pb-3 border-b border-stone-200 dark:border-stone-700">
                            <TextSelect className="w-4 h-4 text-teal-500" />
                            <span className="text-xs text-stone-500 dark:text-stone-400">Use template:</span>
                            <select
                              onChange={(e) => {
                                const template = templates.find((t) => t.id === e.target.value)
                                if (template) handleSelectTemplate(template)
                              }}
                              value=""
                              className="flex-1 px-2 py-1 text-sm rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                              <option value="" disabled>Select a template to apply...</option>
                              {templates.map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.title}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div>
                          <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                            Title
                          </label>
                          <input
                            type="text"
                            value={newCriteriaTitle}
                            onChange={(e) => setNewCriteriaTitle(e.target.value)}
                            placeholder="e.g., Competitor Mention"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            autoFocus
                          />
                          <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                            A short name for this criteria (used in notifications).
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                            Description
                          </label>
                          <textarea
                            value={newCriteriaDescription}
                            onChange={(e) => setNewCriteriaDescription(e.target.value)}
                            placeholder="e.g., Any mention of a competitor"
                            rows={2}
                            className="w-full px-3 py-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                          />
                          <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                            Describe what the AI should look for to flag an item as a gem.
                          </p>
                        </div>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newCriteriaNotify}
                            onChange={(e) => setNewCriteriaNotify(e.target.checked)}
                            className="w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-stone-600 dark:text-stone-400">
                            Notify immediately when found
                          </span>
                        </label>

                        {newCriteriaNotify && (
                          <div className="mt-3 pl-6">
                            <UserCheckboxList
                              users={stream.accountUsers || []}
                              selectedIds={newCriteriaSubscriberIds}
                              onChange={setNewCriteriaSubscriberIds}
                              label="Users to notify"
                            />
                          </div>
                        )}

                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={resetCriteriaForm}
                            className="px-3 py-1.5 text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={saveEditCriteria}
                            disabled={!newCriteriaTitle.trim() || !newCriteriaDescription.trim()}
                            className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-md disabled:opacity-50 transition-colors"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Display card */
                      <div className="py-3 border-b border-stone-100 dark:border-stone-800 last:border-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div
                              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                gc.notifyImmediately
                                  ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
                                  : 'bg-stone-100 dark:bg-stone-800 text-stone-400 dark:text-stone-500'
                              }`}
                            >
                              {gc.notifyImmediately ? (
                                <Bell className="w-3 h-3" />
                              ) : (
                                <BellOff className="w-3 h-3" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-stone-900 dark:text-stone-100">
                                {gc.title}
                              </p>
                              <p className="text-sm text-stone-600 dark:text-stone-400 mt-0.5">
                                {gc.description}
                              </p>
                              <p className="text-xs text-stone-400 dark:text-stone-500 mt-1">
                                {gc.notifyImmediately ? 'Notify immediately' : 'Silent'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Reorder buttons */}
                            <div className="flex flex-col">
                              <button
                                onClick={() => moveCriteria(gc.id, 'up')}
                                disabled={index === 0}
                                className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => moveCriteria(gc.id, 'down')}
                                disabled={index === gemCriteria.length - 1}
                                className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                            </div>
                            <button
                              onClick={() => openSaveAsTemplateModal(gc)}
                              className="p-2 text-stone-400 hover:text-teal-500 transition-colors rounded-md hover:bg-stone-100 dark:hover:bg-stone-800"
                              title="Save as template"
                            >
                              <TextSelect className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => startEditCriteria(gc)}
                              className="p-2 text-stone-400 hover:text-teal-500 transition-colors rounded-md hover:bg-stone-100 dark:hover:bg-stone-800"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeCriteria(gc.id)}
                              className="p-2 text-stone-400 hover:text-red-500 transition-colors rounded-md hover:bg-stone-100 dark:hover:bg-stone-800 flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add new criteria form (only when not editing) */}
                {showCriteriaForm && !editingCriteriaId ? (
                  <div className="mt-3 p-4 rounded-lg bg-stone-50 dark:bg-stone-800/50 space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={newCriteriaTitle}
                        onChange={(e) => setNewCriteriaTitle(e.target.value)}
                        placeholder="e.g., Competitor Mention"
                        className="w-full px-3 py-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        autoFocus
                      />
                      <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                        A short name for this criteria (used in notifications).
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-2">
                        Description
                      </label>
                      <textarea
                        value={newCriteriaDescription}
                        onChange={(e) => setNewCriteriaDescription(e.target.value)}
                        placeholder="e.g., Any mention of a competitor"
                        rows={2}
                        className="w-full px-3 py-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                      />
                      <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                        Describe what the AI should look for to flag an item as a gem.
                      </p>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newCriteriaNotify}
                        onChange={(e) => setNewCriteriaNotify(e.target.checked)}
                        className="w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-sm text-stone-600 dark:text-stone-400">
                        Notify immediately when found
                      </span>
                    </label>

                    {newCriteriaNotify && (
                      <div className="mt-3 pl-6">
                        <UserCheckboxList
                          users={stream.accountUsers || []}
                          selectedIds={newCriteriaSubscriberIds}
                          onChange={setNewCriteriaSubscriberIds}
                          label="Users to notify"
                        />
                      </div>
                    )}

                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={resetCriteriaForm}
                        className="px-3 py-1.5 text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={addCriteria}
                        disabled={!newCriteriaTitle.trim() || !newCriteriaDescription.trim()}
                        className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-md disabled:opacity-50 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                ) : !editingCriteriaId && (
                  <>
                    {showTemplateChoice && templates.length > 0 ? (
                      <div className="mt-3 p-4 rounded-lg bg-stone-50 dark:bg-stone-800/50 space-y-4">
                        <p className="text-sm text-stone-600 dark:text-stone-400">
                          How would you like to add a gem criteria?
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={handleCreateFromScratch}
                            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 hover:border-stone-300 dark:hover:border-stone-600 transition-colors"
                          >
                            <PenLine className="w-5 h-5" />
                            <span className="text-sm font-medium">Create from scratch</span>
                          </button>
                          <div className="relative">
                            <select
                              onChange={(e) => {
                                const template = templates.find((t) => t.id === e.target.value)
                                if (template) handleSelectTemplate(template)
                              }}
                              defaultValue=""
                              className="w-full h-full opacity-0 absolute inset-0 cursor-pointer"
                            >
                              <option value="" disabled>Select template</option>
                              {templates.map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.title}
                                </option>
                              ))}
                            </select>
                            <div className="flex flex-col items-center gap-2 p-4 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 hover:border-stone-300 dark:hover:border-stone-600 transition-colors pointer-events-none">
                              <TextSelect className="w-5 h-5" />
                              <span className="text-sm font-medium">Use a template</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowTemplateChoice(false)}
                          className="w-full text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => templates.length > 0 ? setShowTemplateChoice(true) : setShowCriteriaForm(true)}
                        className="w-full mt-2 py-2 text-sm text-stone-400 dark:text-stone-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                      >
                        + Add criteria
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </CollapsibleSection>
      </main>

      {/* Sticky save bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-amber-500 dark:bg-amber-600 shadow-lg z-20">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
            <p className="text-sm font-medium text-amber-950 dark:text-white">
              You have unsaved changes
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setName(stream.name)
                  setDescription(stream.description || '')
                  setStatus(stream.status)
                  setQuestions(stream.questions)
                  setGemCriteria(stream.gemCriteria)
                  setReportSubscriberIds(stream.reportSubscriberIds || [])
                }}
                className="px-3 py-1.5 text-sm font-medium text-amber-950 dark:text-white hover:text-amber-800 dark:hover:text-amber-100"
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-1.5 text-sm font-medium bg-amber-950 hover:bg-amber-900 text-white rounded-md transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save as template modal */}
      {criteriaToSaveAsTemplate && (
        <SaveAsTemplateModal
          isOpen={saveAsTemplateModalOpen}
          onClose={() => {
            setSaveAsTemplateModalOpen(false)
            setCriteriaToSaveAsTemplate(null)
          }}
          gemCriterion={{
            title: criteriaToSaveAsTemplate.title,
            description: criteriaToSaveAsTemplate.description,
          }}
          templates={templates}
          onSaved={handleTemplateSaved}
        />
      )}

      {/* Confirm overwrite modal */}
      {templateToApply && (
        <ConfirmOverwriteModal
          isOpen={confirmOverwriteModalOpen}
          onClose={() => {
            setConfirmOverwriteModalOpen(false)
            setTemplateToApply(null)
          }}
          onConfirm={handleConfirmApplyTemplate}
          templateTitle={templateToApply.title}
        />
      )}
    </div>
  )
}
